import React, { useState } from 'react';
import _ from 'lodash';
import moment from 'moment';
import { logError } from 'utils/logger';
import { useQuery } from '@apollo/client';
import { useSelector } from 'react-redux';
import { Nullable, SelectOption } from 'global';
import { LooksTwo } from '@mui/icons-material';
import { Box, Paper, Stack, Tooltip, Typography } from '@mui/material';
import { useHistory, useParams } from 'react-router-dom';
import { BusinessRule } from '@trnsact/business-criteria';
import { FormInput, FormRadioGroup, FormSwitch } from 'components/form';
import { PageContainer } from 'components/shared/Page/PageContainer';
import { convertEnumToSelectOptions } from 'utils/selectOptionsHelper';
import { removeGraphQLMEtaFields } from 'utils/removeGraphQLMEtaFields';
import { aftermarketProductsApi } from 'modules/aftermarket/products/api';
import { portalConfigurationsSelectors } from 'redux/portalConfigurationReducer';
import { mapCriteriaToConditions } from 'modules/jsonRulesCriterias/businessCriteria';
import { RenderWithCondition } from 'components/shared/Conditional/RenderWithCondition';
import { DocumentsCard } from 'modules/aftermarket/products/ui/documentsCard/DocumentsCard';
import { AFTERMARKET_PRODUCT_EVENTS } from '@trnsact/business-criteria/dist/src/models/rule-event/aftermarket/aftermarket-product-events';
import {
  AftermarketLicenceType,
  AftermarketProductType,
  AftermarketProductCategory,
} from '@trnsact/trnsact-shared-types';
import {
  AftermarketProductPermissions,
  Configuration,
  getInitialConfigKeysFromCriteria,
  GetPartnerLinksResponse,
  GetPartnerLinksVariables,
  mapListingToConfiguration,
  mapProductConfigToListing,
  useAftermarketProducts,
  PageHeader,
  LogoSection,
  VendorSection,
  AvailabilitySection,
  ConfigurationSection,
  EditRestrictionReasonMessage,
} from 'modules/aftermarket';
import {
  ProductConfig,
  EditRestrictionReason,
  ProductPageDocumentsState,
} from 'modules/aftermarket/products/types/products';
import {
  MutationDTO,
  AftermarketProductCatalogDetails,
  GetAftermarketProductByIdResponse,
  GetAftermarketProductByIdVariables,
} from 'modules/aftermarket/products/api/types';

export const AftermarketProductPage = () => {
  const history = useHistory();
  const { aftermarketProductId } = useParams<{ aftermarketProductId: string | undefined }>();

  const account = useSelector((state: any) => state.account);
  const isPartnerAccount = account.accountType === 'Partner';

  const aftermarketLicenceType = useSelector(portalConfigurationsSelectors.aftermarketLicenceType);
  const canWriteByLicenseType = [AftermarketLicenceType.Enterprise, AftermarketLicenceType.Pro].includes(
    aftermarketLicenceType
  );
  const canWriteByPortalConfigurations = useSelector(portalConfigurationsSelectors.hasAftermarketWriteAccess);

  const { handleAftermarketProductUpdate, handleAftermarketProductCreate, isLoading } = useAftermarketProducts();

  const [configurations, setConfigurations] = useState<Configuration[]>([]);
  const [existingUniqueCriteriaFacts, setExistingUniqueCriteriaFacts] = useState<string[]>([]);
  const [mappedProductConfigForListing, setMappedProductConfigForListing] = useState<Nullable<ProductConfig[]>>(
    aftermarketProductId ? null : []
  );
  const [editRestrictionReason, setEditRestrictionReason] = useState<EditRestrictionReason>();

  const { data: partnerLinksResponse, loading: isPartnerLinksLoading } = useQuery<
    GetPartnerLinksResponse,
    GetPartnerLinksVariables
  >(aftermarketProductsApi.getPartnerLinks, {
    variables: {
      accountId: account.id,
      withDealerMetaData: true,
    },
  });

  const {
    error: productError,
    data: productResponse,
    loading: isProductLoading,
  } = useQuery<GetAftermarketProductByIdResponse, GetAftermarketProductByIdVariables>(
    aftermarketProductsApi.getProductsById,
    {
      skip: !aftermarketProductId,
      variables: {
        accountId: account.id,
        productId: aftermarketProductId!,
      },
      onCompleted({ aftermarketProductById: product }) {
        if (!product) return;

        const isProductCreatedByCurrentAccount = product.accountIdCreatedBy === account.id;

        const canWriteByUserRole = isPartnerAccount || canWriteByPortalConfigurations;
        const canWriteByLicenseTypeOrAccountType = isPartnerAccount || canWriteByLicenseType;

        setProductName(product.productName);
        setCanWrite(isProductCreatedByCurrentAccount && canWriteByUserRole && canWriteByLicenseTypeOrAccountType);

        if (!isProductCreatedByCurrentAccount && !!aftermarketProductId) {
          setEditRestrictionReason(EditRestrictionReason.SHARED_PRODUCT);
        } else if (!canWriteByLicenseTypeOrAccountType) {
          setEditRestrictionReason(EditRestrictionReason.LICENSE_TYPE);
        } else if (!canWriteByUserRole) {
          setEditRestrictionReason(EditRestrictionReason.USER_ROLE);
        }

        if (product.aftermarketVendorApiChannel) setProductManagedByApi(true);
        if (product.externalId) setExternalId(product.externalId);

        setBaseOrOption(product.productType);

        setProductCatalog(
          removeGraphQLMEtaFields(product.aftermarketProductCatalogDetails as AftermarketProductCatalogDetails)
        );

        setProductLogoUrl(product.productLogoUrl);
        setFirstDateAvailable(product.firstDateAvailable ?? null);
        setLastDateAvailable(product.lastDateAvailable ?? null);
        setIsActive(product.isActive !== false);

        const chosenVendors = product.partnerLinks.map((pl: any) => {
          const v = partnerLinksResponse?.aftermarketDealerPartnerLink?.find(
            ({ partnerLinkId }) => partnerLinkId === pl.partnerLinkId
          );

          return {
            label: _.get(v, 'partnerProfile.name', 'Unknown Vendor'),
            value: pl.partnerLinkId,
          };
        });

        if (chosenVendors) {
          setSelectedVendor(chosenVendors);
        }

        setProductType(product.productCategory);

        const mappedConfig = mapProductConfigToListing(product);

        setExistingUniqueCriteriaFacts(getInitialConfigKeysFromCriteria(product?.criteria ?? []));
        setMappedProductConfigForListing(mappedConfig);
        setConfigurations(mappedConfig.map((listItem, index) => mapListingToConfiguration(listItem, index)));
      },
    }
  );

  const [productLogo, setProductLogo] = useState<File | null>(null);
  const [productLogoPreview, setProductLogoPreview] = useState<string | null>(null);

  const [canWrite, setCanWrite] = useState(true);

  const [selectedVendor, setSelectedVendor] = useState<SelectOption[]>([]);
  const [selectedOptions, setSelectedOptions] = useState<SelectOption[]>([]);
  const [productName, setProductName] = useState<string>('');
  const [externalId, setExternalId] = useState<string | null>(null);
  const [productType, setProductType] = useState<string>(AftermarketProductCategory.Gap);
  const [baseOrOption, setBaseOrOption] = useState<AftermarketProductType>(AftermarketProductType.Product);
  const [productManagedByApi, setProductManagedByApi] = useState<boolean>(false);
  const [documents, setDocuments] = useState<ProductPageDocumentsState>({
    initialDocuments: [],
    documentsToAdd: [],
    documentsToRemove: [],
  });

  const [productCatalog, setProductCatalog] = useState<AftermarketProductCatalogDetails>({
    productSummary: '',
    legalDisclaimer: '',
    coverageDetails: '',
    paragraphDescription: '',
    limitationsOfLiability: '',
    eligibilityAndInclusions: '',
  });

  const [productLogoUrl, setProductLogoUrl] = useState<string | null>(null);
  const [firstDateAvailable, setFirstDateAvailable] = useState<string | null>(null);
  const [lastDateAvailable, setLastDateAvailable] = useState<string | null>(null);
  const [dateValidationError, setDateValidationError] = useState<boolean>(false);
  const [isActive, setIsActive] = useState<boolean>(true);

  // Same rule as the API: Core accounts can't change the status, nor can anyone on products managed by a vendor API
  const canChangeIsActive = canWrite && (isPartnerAccount || canWriteByLicenseType) && !productManagedByApi;

  const validateDates = (firstDate: string | null, lastDate: string | null) => {
    if (firstDate && lastDate) {
      const first = moment(firstDate);
      const last = moment(lastDate);
      return last.isBefore(first);
    }

    return false;
  };

  const handleProductLogoChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const selectedFile = event.target.files?.[0] || null;

    if (selectedFile) {
      const reader = new FileReader();

      reader.onloadend = () => {
        setProductLogoPreview(reader.result?.toString() ?? null);
      };
      reader.readAsDataURL(selectedFile);
    } else {
      setProductLogoPreview(null);
    }

    setProductLogo(selectedFile);
  };

  const handleSaveAftermarketProduct = async () => {
    const rules = configurations.map(config => {
      const rule = new BusinessRule({
        name: config.name,
        eventParams: {
          type: AFTERMARKET_PRODUCT_EVENTS.AFTERMARKET_PRODUCT_QUALIFIED,
          params: {
            DEALER_COST: config.dealerCost,
            MARKUP: {
              AMOUNT: config.dealerMarkup,
              TYPE: config.dealerMarkupType,
            },
            RATE_FACTOR: config.rateFactor,
            PRICING_MAX: config.maxPricing,
            PRICING_MIN: config.minPricing,
            SUGGESTED_RETAIL_PRICE: config.suggestedRetailPricing,
          },
        },
      });

      const conditions = Object.values(config.configRows);
      const conditionsForSave = mapCriteriaToConditions(conditions);
      const conditionsForSaveWithRequiredForQuote = conditionsForSave.map(condition => ({
        ...condition,
        requiredForQuote: true,
      }));
      conditionsForSaveWithRequiredForQuote.forEach(condition => rule.Conditions.addCondition(condition));

      return rule;
    });

    try {
      const dto: MutationDTO = {
        productLogo,
        productType: baseOrOption,
        productDescriptionInternal: null,
        productName: productName?.trim(),
        productCategory: productType as any, // Yes, productType is the category
        aftermarketProductCatalogDetails: productCatalog,
        addonIds: selectedOptions.map(option => option.value),
        criteria: rules.map(rule => rule.getRule()),
        partnerLinkIds: selectedVendor.map(vendor => vendor.value),
        ...(canChangeIsActive ? { isActive } : {}),
        ...(externalId ? { externalId } : {}),
        ...(lastDateAvailable ? { lastDateAvailable } : {}),
        ...(firstDateAvailable ? { firstDateAvailable } : {}),
      };

      if (aftermarketProductId) {
        await handleAftermarketProductUpdate({ aftermarketProductId, ...dto }, documents);
      } else {
        await handleAftermarketProductCreate({ ...dto, sourceOfCreation: 'base' }, documents);
      }
    } catch (error) {
      logError(error);
    }

    history.push('/aftermarket');
  };

  const isSubmitDisabled =
    !selectedVendor.length ||
    !productName ||
    (!configurations.length && !productManagedByApi && !isPartnerAccount) ||
    dateValidationError;

  return (
    <PageContainer title="Aftermarket Product" loading={isProductLoading} error={productError}>
      <Stack gap="1rem">
        <PageHeader
          canWrite={canWrite}
          isLoading={isLoading}
          onBack={history.goBack}
          isActionDisabled={isSubmitDisabled}
          aftermarketProductId={aftermarketProductId}
          handleSaveAftermarketProduct={handleSaveAftermarketProduct}
        />

        <EditRestrictionReasonMessage reason={editRestrictionReason} />

        <VendorSection
          canWrite={canWrite}
          selectedVendor={selectedVendor}
          isPartnerAccount={isPartnerAccount}
          setSelectedVendor={setSelectedVendor}
          partnerLinksResponse={partnerLinksResponse}
          isPartnerLinksLoading={isPartnerLinksLoading}
        />

        <Paper sx={{ padding: '1rem' }}>
          <Stack gap="1rem">
            <Typography variant="subtitle1" color="primary">
              <LooksTwo /> Product Configuration
            </Typography>

            <FormInput
              required
              variant="outlined"
              value={productName}
              disabled={!canWrite}
              onChange={value => setProductName(value)}
              label="Product Name - The applicant will be presented with this name"
            />

            <Tooltip title="Inactive products are not offered on new deals" placement="top-start">
              <Box width="fit-content">
                <FormSwitch
                  checked={isActive}
                  disabled={!canChangeIsActive}
                  label="Active"
                  onChange={({ target }) => setIsActive(target.checked)}
                />
              </Box>
            </Tooltip>

            <RenderWithCondition condition={productManagedByApi}>
              <FormInput
                disabled
                variant="outlined"
                value={externalId || ''}
                onChange={value => setExternalId(value)}
                label="Vendor's Product ID - Used for API Integration"
              />
            </RenderWithCondition>

            <AftermarketProductPermissions licenceType={[AftermarketLicenceType.Enterprise]}>
              <LogoSection
                canWrite={canWrite}
                productLogoUrl={productLogoUrl}
                setProductLogo={setProductLogo}
                productLogoPreview={productLogoPreview}
                setProductLogoPreview={setProductLogoPreview}
                handleProductLogoChange={handleProductLogoChange}
              />
            </AftermarketProductPermissions>

            <FormRadioGroup
              row
              disabled={!canWrite}
              value={productType}
              label="Product Type"
              options={convertEnumToSelectOptions(AftermarketProductCategory)}
              onChange={(_, value) => setProductType(value)}
            />

            <AftermarketProductPermissions licenceType={[AftermarketLicenceType.Enterprise]}>
              <FormRadioGroup
                row
                disabled={!canWrite}
                value={baseOrOption}
                label="Product or Addon?"
                options={convertEnumToSelectOptions(AftermarketProductType)}
                onChange={(_, value) => {
                  setBaseOrOption(value as AftermarketProductType);
                }}
              />
            </AftermarketProductPermissions>

            <AftermarketProductPermissions licenceType={[AftermarketLicenceType.Enterprise]}>
              <AvailabilitySection
                canWrite={canWrite}
                validateDates={validateDates}
                lastDateAvailable={lastDateAvailable}
                firstDateAvailable={firstDateAvailable}
                dateValidationError={dateValidationError}
                setLastDateAvailable={setLastDateAvailable}
                setFirstDateAvailable={setFirstDateAvailable}
                setDateValidationError={setDateValidationError}
              />
            </AftermarketProductPermissions>

            <ConfigurationSection
              canWrite={canWrite}
              accountId={account.id}
              baseOrOption={baseOrOption}
              productCatalog={productCatalog}
              selectedOptions={selectedOptions}
              setProductCatalog={setProductCatalog}
              setConfigurations={setConfigurations}
              setSelectedOptions={setSelectedOptions}
              aftermarketProductId={aftermarketProductId}
              product={productResponse?.aftermarketProductById}
              existingUniqueCriteriaFacts={existingUniqueCriteriaFacts}
              mappedProductConfigForListing={mappedProductConfigForListing}
            />
          </Stack>
        </Paper>

        <AftermarketProductPermissions licenceType={[AftermarketLicenceType.Enterprise]}>
          <DocumentsCard
            canWrite={canWrite}
            documents={documents}
            setDocuments={setDocuments}
            aftermarketProductId={aftermarketProductId}
          />
        </AftermarketProductPermissions>
      </Stack>
    </PageContainer>
  );
};
